import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CalendarDay } from 'further-ui/utils';
import { realisation as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';

export default function useSummaryData({
  vehicleId,
  assetId,
  realisationDate,
  holdingClassInvestmentsWithUnitSold,
  currentRealisationPerHoldingClassInvestment,
}: {
  vehicleId: string;
  assetId: string;
  realisationDate: CalendarDay;
  holdingClassInvestmentsWithUnitSold: rules.CalculateSaleOfInvestmentInputValues['holdingClassInvestments'];
  currentRealisationPerHoldingClassInvestment: Array<{
    investmentId: string;
    holdingClassId: string;
    unitsSold: number;
  }>;
}) {
  const calculateSaleOfInvestment =
    api.lpRealisation.calculateSaleOfInvestment.useMutation();
  const getUnrealisedGainsUnwind =
    api.lpRealisation.getUnrealisedGainsUnwind.useMutation();
  const getUnrealisedAssetFxUnwinds =
    api.unrealisedAssetFxGainOrLoss.getUnwind.useMutation();

  const result = useQuery({
    queryKey: [
      'realisationSummary',
      {
        params: {
          vehicleId,
          assetId,
          realisationDate,
          holdingClassInvestmentsWithUnitSold,
          currentRealisationPerHoldingClassInvestment,
        },
      },
    ],
    queryFn: async () => {
      const [
        saleOfInvestmentData,
        unrealisedGainsUnwindData,
        unrealisedAssetFXGainsAndLossesUnwinds,
      ] = await Promise.all([
        calculateSaleOfInvestment.mutateAsync({
          vehicleId,
          holdingClassInvestments: holdingClassInvestmentsWithUnitSold,
        }),
        getUnrealisedGainsUnwind.mutateAsync({
          assetId,
          vehicleId,
          realisationDate,
          currentRealisationPerHoldingClassInvestment,
        }),
        getUnrealisedAssetFxUnwinds.mutateAsync({
          vehicleId,
          holdingClassRealisations: currentRealisationPerHoldingClassInvestment,
          date: realisationDate,
        }),
      ]);

      return {
        saleOfInvestmentData,
        unrealisedGainsUnwindData,
        unrealisedAssetFXGainsAndLossesUnwinds,
      };
    },
    placeholderData: keepPreviousData,
    gcTime: 0,
  });

  return {
    data: result.data ?? null,
    isLoading: result.isFetching || result.isLoading,
    isError: result.isError,
  };
}
